import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const SERIES_COLORS = [
  "#6F3AFF",
  "#52ea91",
  "#ffb347",
  "#39c0ed",
  "#ff6f91",
  "#8dd3c7",
  "#c9a227",
  "#a78bfa",
];

const TICK = { fill: "#d6d5ea", fontSize: 12 };

function formatDate(dateStr) {
  if (!dateStr) return "";

  const d = new Date(`${dateStr}T00:00:00`);

  if (Number.isNaN(d.getTime())) {
    return String(dateStr);
  }

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatInt(n) {
  return Number(n ?? 0).toLocaleString("de-DE");
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getStepColor(stepName, index) {
  const safeIndex = Number.isFinite(index) ? index : 0;
  return SERIES_COLORS[safeIndex % SERIES_COLORS.length];
}

function getStepLabel(stepName) {
  return `Step ${stepName}`;
}

function getRowsArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
}

function getSeriesNames(raw, rows) {
  const fromMetadata =
    raw?.metadata?.series_names ||
    raw?.series_names ||
    raw?.data?.metadata?.series_names ||
    [];

  const names = new Set(fromMetadata.map((name) => String(name)));

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (/^\d+(\.\d+)?$/.test(key)) {
        names.add(String(key));
      }
    });
  });

  return Array.from(names);
}

function normalizeRows(raw) {
  const sourceRows = getRowsArray(raw);
  const seriesNames = getSeriesNames(raw, sourceRows);

  const rows = sourceRows.map((row) => {
    const mapped = {
      date: row.date || row.day || row.period || row.label || "",
    };

    seriesNames.forEach((stepName) => {
      mapped[stepName] = toNumber(row?.[stepName], 0);
    });

    return mapped;
  });

  return {
    rows,
    seriesNames,
  };
}

function CustomTooltip({ active, payload, label, seriesNames }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="remediation-dashboard-tooltip">
      <div className="remediation-dashboard-tooltip-title">
        {formatDate(label)}
      </div>

      {seriesNames.map((stepName, idx) => {
        const stepValue = payload.find((p) => p.dataKey === stepName)?.value;
        if (stepValue == null) return null;

        return (
          <div
            key={stepName}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 24,
              marginBottom: 4,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: getStepColor(stepName, idx),
                  display: "inline-block",
                }}
              />
              {getStepLabel(stepName)}
            </span>
            <span style={{ fontWeight: 600 }}>{formatInt(stepValue)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CaseTrendByStep() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [seriesNames, setSeriesNames] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetchWithAuth(
          `${API_BASE}/ainstein/in/dashboard?chart_key=case_trend_by_step`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const statsData = await response.json();
        const normalized = normalizeRows(statsData);

        if (!cancelled) {
          setRows(normalized.rows);
          setSeriesNames(normalized.seriesNames);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load remediation step trend");
          setRows([]);
          setSeriesNames([]);
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSeries = useMemo(() => seriesNames.slice(0, 8), [seriesNames]);

  return (
    <section className="remediation-dashboard-section">
      <article className="remediation-dashboard-card">
        <h3 className="remediation-dashboard-card-title">
          Remediation Step Trend
        </h3>

        {loading && (
          <div className="remediation-dashboard-loading">
            <span className="remediation-dashboard-loading-spinner" />
            <span>Loading data...</span>
          </div>
        )}

        {!loading && (error || rows.length === 0 || visibleSeries.length === 0) && (
          <div className="remediation-dashboard-empty-state">
            {error || "No data available"}
          </div>
        )}

        {!loading && !error && rows.length > 0 && visibleSeries.length > 0 && (
          <MeasuredChartFrame height={320}>
            {({ width, height }) => (
              <LineChart
                width={width}
                height={height}
                data={rows}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                  tickFormatter={formatInt}
                />
                <Tooltip
                  cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                  content={<CustomTooltip seriesNames={visibleSeries} />}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: 12,
                    color: "#d6d5ea",
                    paddingTop: 8,
                  }}
                  formatter={(value) => (
                    <span style={{ color: "#e5e8f3" }}>{value}</span>
                  )}
                />

                {visibleSeries.map((stepName, idx) => {
                  const color = getStepColor(stepName, idx);

                  return (
                    <Line
                      key={stepName}
                      type="monotone"
                      dataKey={stepName}
                      name={getStepLabel(stepName)}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: color, strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: color }}
                    />
                  );
                })}
              </LineChart>
            )}
          </MeasuredChartFrame>
        )}
      </article>
    </section>
  );
}